import moment from 'moment';
import { Group, Text, Rect, Line } from 'react-konva';
import { PLATFORM_THEME_COLORS } from '@thinknet-gmbh/platform-common-ui';
import { COLUMN_HEIGHT } from 'src/pages/gantt/gantt-calendar/gantt-view/gantt-view.constants';
import {
  HEADER_ROW_HEIGHT,
  DATE_COLUMN_WIDTH,
  DATE_COLUMN_INDENT,
} from 'src/pages/gantt/gantt-calendar/gantt-view/gantt-view.constants';
import {
  GanttColumnTypesEnum,
  GANTT_DISABLED_DAY_COLUMN_FOR_TYPES,
} from 'src/pages/gantt/gantt.constants';
import { IViewDate } from 'src/pages/gantt/gantt-calendar/gantt-view/gantt-view.interfaces';
import { getRowStatusColor } from 'src/pages/gantt/gantt-calendar/gantt-view/components/status-columns-layer/status-columns-layer.helpers';
import { CurrentNotActiveDayColumn } from 'src/pages/gantt/gantt-calendar/gantt-view/components/status-columns-layer/current-not-active-day-column';

import { IDateColumnsLayer } from './date-columns-layer.interfaces';
import { useTranslation } from 'react-i18next';

const {
  primary: { grey },
  grayVariations: { murky, medium, light },
} = PLATFORM_THEME_COLORS;

const DateColumnsLayer = ({
  colorSchema,
  fullHeight,
  dateValues,
  viewFormat,
}: IDateColumnsLayer) => {
  const { i18n } = useTranslation();

  return (
    <>
      {dateValues.map(({ date, types, index }: IViewDate) => {
        const [weekDay, dateFormat] = moment(date)
          .locale(i18n.language)
          .format(viewFormat)
          .replace('.', '')
          .split(' ');

        const endTopColumnPosition = index * COLUMN_HEIGHT + HEADER_ROW_HEIGHT;
        const endBottomColumnPosition = endTopColumnPosition + COLUMN_HEIGHT;
        const isCurrentDay = types.includes(GanttColumnTypesEnum.CURRENT_DAY);
        const isNotActiveDay = types.some(type =>
          GANTT_DISABLED_DAY_COLUMN_FOR_TYPES.includes(type)
        );
        const color = getRowStatusColor(types, colorSchema);

        return (
          <Group key={index} listening={false}>
            <Rect
              x={0}
              width={DATE_COLUMN_WIDTH}
              perfectDrawEnabled={false}
              y={endTopColumnPosition}
              height={COLUMN_HEIGHT}
              fill={color}
            />

            {/* TODO: We need better solution for hatch lines square */}
            {isCurrentDay && isNotActiveDay && (
              <>
                <CurrentNotActiveDayColumn
                  colorSchema={colorSchema}
                  isHoliday={types.includes(GanttColumnTypesEnum.HOLYDAY)}
                  elementPoints={[
                    0,
                    endTopColumnPosition,
                    DATE_COLUMN_WIDTH / 2,
                    endBottomColumnPosition,
                  ]}
                />
                <CurrentNotActiveDayColumn
                  colorSchema={colorSchema}
                  isHoliday={types.includes(GanttColumnTypesEnum.HOLYDAY)}
                  elementPoints={[
                    DATE_COLUMN_WIDTH / 2,
                    endTopColumnPosition,
                    DATE_COLUMN_WIDTH,
                    endBottomColumnPosition,
                  ]}
                />
              </>
            )}

            <Text
              x={0}
              perfectDrawEnabled={false}
              width={isCurrentDay ? 34 : 30}
              fontSize={12}
              font
              text={`${weekDay}.`}
              fontStyle={isCurrentDay ? 'bold' : 'normal'}
              verticalAlign="middle"
              fontFamily="Inter"
              height={COLUMN_HEIGHT}
              y={endTopColumnPosition}
              offsetX={-DATE_COLUMN_INDENT}
              fill={grey}
            />
            <Text
              x={isCurrentDay ? 43 : 40}
              width={isCurrentDay ? 40 : 44}
              align="left"
              fontSize={12}
              fontFamily="Inter"
              perfectDrawEnabled={false}
              fontStyle={isCurrentDay ? 'bold' : 'normal'}
              text={dateFormat}
              height={COLUMN_HEIGHT}
              verticalAlign="middle"
              y={endTopColumnPosition}
              fill={murky}
            />
            <Line
              stroke={light}
              strokeWidth={2}
              perfectDrawEnabled={false}
              points={[0, endBottomColumnPosition, DATE_COLUMN_WIDTH, endBottomColumnPosition]}
            />
          </Group>
        );
      })}
      <Line
        strokeWidth={1}
        stroke={medium}
        points={[DATE_COLUMN_WIDTH, HEADER_ROW_HEIGHT, DATE_COLUMN_WIDTH, fullHeight]}
      />
    </>
  );
};

export default DateColumnsLayer;
